/**
 * AURA Profile Button Control
 */

import { getIcon } from '../styles/icons';

export interface ProfileButtonOptions {
  id: string;
  name: string;
  description: string;
  icon: string;
  active?: boolean;
  onToggle?: (active: boolean) => void;
}

const PROFILE_BUTTON_STYLES = `
  .aura-profile-btn {
    display: flex;
    align-items: center;
    gap: var(--aura-spacing-md);
    width: 100%;
    padding: var(--aura-spacing-md);
    background: var(--aura-bg-secondary);
    border-radius: var(--aura-radius-md);
    transition: background-color var(--aura-transition-fast), box-shadow var(--aura-transition-fast);
    text-align: left;
    min-height: var(--aura-touch-target);
  }

  .aura-profile-btn:hover {
    background: var(--aura-bg-hover);
  }

  .aura-profile-btn--active {
    background: var(--aura-primary-light);
    box-shadow: inset 0 0 0 2px var(--aura-primary);
  }

  .aura-profile-btn--active:hover {
    background: var(--aura-primary-light);
  }

  .aura-profile-btn:focus-visible {
    outline: 2px solid var(--aura-border-focus);
    outline-offset: 2px;
  }

  .aura-profile-btn__toggle {
    display: flex;
    gap: var(--aura-spacing-xs);
  }

  .aura-profile-btn__toggle-option {
    padding: var(--aura-spacing-xs) var(--aura-spacing-sm);
    border-radius: var(--aura-radius-sm);
    font-size: var(--aura-font-size-sm);
    font-weight: 500;
    color: var(--aura-text-muted);
    background: transparent;
    transition: all var(--aura-transition-fast);
  }

  .aura-profile-btn__toggle-option--active {
    background: var(--aura-secondary, #4a90d9);
    color: var(--aura-text-inverse);
  }

  .aura-profile-btn__icon {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--aura-bg);
    border-radius: var(--aura-radius-md);
    color: var(--aura-primary);
    transition: background-color var(--aura-transition-fast), color var(--aura-transition-fast);
    flex-shrink: 0;
  }

  .aura-profile-btn--active .aura-profile-btn__icon {
    background: var(--aura-primary);
    color: var(--aura-text-inverse);
  }

  .aura-profile-btn__icon svg {
    width: 24px;
    height: 24px;
  }

  .aura-profile-btn__content {
    flex: 1;
    min-width: 0;
  }

  .aura-profile-btn__name {
    font-size: var(--aura-font-size-md);
    font-weight: 600;
    color: var(--aura-text);
    margin-bottom: 2px;
  }

  .aura-profile-btn__desc {
    font-size: var(--aura-font-size-sm);
    color: var(--aura-text-muted);
  }
`;

export function createProfileButton(options: ProfileButtonOptions): { element: HTMLElement; styles: string; setActive: (a: boolean) => void } {
  let isActive = options.active || false;

  const container = document.createElement('div');
  container.className = `aura-profile-btn${isActive ? ' aura-profile-btn--active' : ''}`;
  container.setAttribute('role', 'group');
  container.setAttribute('aria-labelledby', `${options.id}-name`);

  const icon = document.createElement('div');
  icon.className = 'aura-profile-btn__icon';
  icon.setAttribute('aria-hidden', 'true');
  icon.innerHTML = getIcon(options.icon as any) || getIcon('accessibility');

  const content = document.createElement('div');
  content.className = 'aura-profile-btn__content';

  const name = document.createElement('div');
  name.className = 'aura-profile-btn__name';
  name.id = `${options.id}-name`;
  name.textContent = options.name;

  const desc = document.createElement('div');
  desc.className = 'aura-profile-btn__desc';
  desc.textContent = options.description;

  content.appendChild(name);
  content.appendChild(desc);

  const toggle = document.createElement('div');
  toggle.className = 'aura-profile-btn__toggle';

  const offBtn = document.createElement('button');
  offBtn.type = 'button';
  offBtn.className = `aura-profile-btn__toggle-option${!isActive ? ' aura-profile-btn__toggle-option--active' : ''}`;
  offBtn.textContent = 'OFF';
  offBtn.setAttribute('aria-pressed', String(!isActive));

  const onBtn = document.createElement('button');
  onBtn.type = 'button';
  onBtn.className = `aura-profile-btn__toggle-option${isActive ? ' aura-profile-btn__toggle-option--active' : ''}`;
  onBtn.textContent = 'ON';
  onBtn.setAttribute('aria-pressed', String(isActive));

  toggle.appendChild(offBtn);
  toggle.appendChild(onBtn);

  container.appendChild(icon);
  container.appendChild(content);
  container.appendChild(toggle);

  const setActive = (active: boolean) => {
    isActive = active;
    container.classList.toggle('aura-profile-btn--active', active);
    offBtn.classList.toggle('aura-profile-btn__toggle-option--active', !active);
    onBtn.classList.toggle('aura-profile-btn__toggle-option--active', active);
    offBtn.setAttribute('aria-pressed', String(!active));
    onBtn.setAttribute('aria-pressed', String(active));
  };

  offBtn.addEventListener('click', () => {
    if (isActive) {
      setActive(false);
      options.onToggle?.(false);
    }
  });

  onBtn.addEventListener('click', () => {
    if (!isActive) {
      setActive(true);
      options.onToggle?.(true);
    }
  });

  return {
    element: container,
    styles: PROFILE_BUTTON_STYLES,
    setActive,
  };
}

export { PROFILE_BUTTON_STYLES };
